import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const empty = { name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", is_default: false };

export default function AddressDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(editing ? { ...empty, ...editing } : empty);
  }, [editing, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(form.pincode)) return toast.error("Please enter a valid 6-digit PIN code.");
    if (!/^\d{10}$/.test(form.phone)) return toast.error("Please enter a valid 10-digit phone number.");
    setSaving(true);
    try {
      if (editing?.id) await api.put(`/addresses/${editing.id}`, form);
      else await api.post("/addresses", form);
      toast.success("Address saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="address-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Edit Address" : "Add New Address"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Full Name</Label>
              <Input value={form.name} onChange={set("name")} required data-testid="address-name" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={set("phone")} required data-testid="address-phone" />
            </div>
          </div>
          <div>
            <Label>Address Line 1</Label>
            <Input value={form.line1} onChange={set("line1")} required data-testid="address-line1" />
          </div>
          <div>
            <Label>Address Line 2 (optional)</Label>
            <Input value={form.line2} onChange={set("line2")} data-testid="address-line2" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={set("city")} required data-testid="address-city" />
            </div>
            <div>
              <Label>State</Label>
              <Input value={form.state} onChange={set("state")} required data-testid="address-state" />
            </div>
            <div>
              <Label>PIN Code</Label>
              <Input value={form.pincode} onChange={set("pincode")} required data-testid="address-pincode" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={form.is_default} onCheckedChange={(v) => setForm((f) => ({ ...f, is_default: !!v }))} data-testid="address-default" />
            Set as default delivery address
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="address-save">{saving ? "Saving…" : "Save Address"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
